import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Select,
  Table,
  Space,
} from "antd";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import {
  createSaleInvoice,
  getCustomers,
  getMyProfile,
} from "../../../network/network";
import moment from "moment";
import {
  AddonName,
  UserType,
} from "../../../configs/constants";
import { AppContext } from "../../../context/AppContextProvider";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import {
  DeleteOutlined,
  InfoCircleOutlined,
  TagsOutlined,
} from "@ant-design/icons";
import TaxCalculator from "../../../components/TaxCalculator";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

import QuantityStepper from "../../../components/QuantityStepper";
import TagOptionsModal from "../POS/components/TagOptionsModal";
const AddInvoice = ({ reloadCallback }) => {
  const { authUser, appConfig, roundOff } = useContext(AppContext);
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [customerData, setCustomerData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [productsData, setProductsData] = useState([]);
  const [cart, setCart] = useState([]);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [selectedProductForTags, setSelectedProductForTags] = useState(null);
  const [selectedTagsForModal, setSelectedTagsForModal] = useState([]);
  const { t } = useTranslation();
  const [addInvoiceForm] = Form.useForm();

  const is_stock_management_active = authUser.active_addons.some(
    (addon) => addon.id && addon.name === AddonName.Stock,
  );

  const getAvailableTags = (product) => {
    if (
      Array.isArray(product?.available_tags) &&
      product.available_tags.length > 0
    ) {
      return product.available_tags;
    }

    if (Array.isArray(product?.tags)) {
      return product.tags;
    }

    return [];
  };

  const normalizeTagIds = (values) => {
    if (!Array.isArray(values)) return [];
    return values
      .map((value) => (value && typeof value === "object" ? value.id : value))
      .filter((value) => value !== undefined && value !== null);
  };

  const hasTagId = (ids = [], id) =>
    ids.some((tagId) => String(tagId) === String(id));

  const tagIdsFrom = (tags = []) =>
    normalizeTagIds(
      tags.map((tag) => (tag && tag.id !== undefined ? tag.id : tag)),
    );

  const makeVariantKey = (productId, tagIds = []) =>
    `${productId}_${normalizeTagIds(tagIds).map(String).sort().join(",") || "no_tags"}`;

  const resetTagModal = () => {
    setTagModalVisible(false);
    setModalMode("add");
    setSelectedProductForTags(null);
    setSelectedTagsForModal([]);
  };

  const buildInvoiceItem = (productBase, tagIds = []) => {
    const normalizedTagIds = normalizeTagIds(tagIds);
    const availableTags = getAvailableTags(productBase);
    const selectedTags = availableTags.filter((tag) =>
      hasTagId(normalizedTagIds, tag.id),
    );

    return {
      ...productBase,
      product_id: productBase.product_id || productBase.id,
      available_tags: availableTags,
      tags: selectedTags,
      selectedTagIds: normalizedTagIds,
      cart_key:
        productBase.cart_key ||
        makeVariantKey(
          productBase.product_id || productBase.id,
          normalizedTagIds,
        ),
    };
  };

  useEffect(() => {
    let categories = [
      { id: 0, name: t("all"), name_ar: t("all", { lng: "ar" }) },
    ];
    let products = [];
    getMyProfile()
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          data.data.user.categories.forEach((category) => {
            if (category.products.length > 0) {
              categories.push({
                id: category.id,
                name: category.name,
                name_ar: category.name_ar,
              });
              products = [...products, ...category.products];
            }
          });
          setProductsData(products);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  }, [t]);

  useEffect(() => {
    setLoadingBTN(true);
    getCustomers({
      per_page: -1,
    })
      .then((response) => {
        let data = response.data || response.response.data;
        if (data.success === true) {
          setCustomerData(response.data.data.customers);
        } else if (data?.success === false) {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoadingBTN(false);
      });
  }, [t]);

  useEffect(() => {
    setLoading(true);
    let tax = 0;
    let total = 0;

    cart.forEach((product) => {
      const productSubTotal = product.price * product.quantity;
      let productTax = 0;

      if ((product.is_taxable || product.tax > 0) && product.taxes?.length) {
        // Calculate each tax separately
        product.taxes.forEach((tax) => {
          productTax += productSubTotal * (Number(tax.rate) / 100);
        });
      }

      tax += productTax;
      total += productSubTotal + productTax;
    });

    setTotalTax(roundOff(tax));
    setTotalAmount(roundOff(total));
    setLoading(false);
  }, [cart, roundOff]);

  const showModal = () => {
    // Reset Form Fields
    addInvoiceForm.resetFields();
    setCart([]);
    resetTagModal();
    setModalVisible(true);
  };

  const handleCancel = () => {
    setCart([]);
    resetTagModal();
    // Reset Form Fields
    addInvoiceForm.resetFields();
    setModalVisible(false);
  };

  const handleAdd = (status) => {
    addInvoiceForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoadingBTN(true);
        values.items = JSON.stringify(
          cart.map((item) => ({
            id: item.product_id,
            qty: item.quantity,
            price: item.price,
            tags:
              item.tags && item.tags.length > 0
                ? tagIdsFrom(item.tags)
                : normalizeTagIds(item.selectedTagIds),
          })),
        );

        values.invoice_due_date = moment.utc(values.invoice_due_date).set({
          date: values.invoice_due_date.date(),
          month: values.invoice_due_date.month(),
          year: values.invoice_due_date.year(),
          hour: 18,
          minute: 59,
          second: 59,
          millisecond: 0,
        });

        values.sale_invoice_status = status;
        // Sending Request to API
        createSaleInvoice(values)
          .then((response) => {
            let data = response.data || response.response.data;
            if (data.success === true) {
              // Success
              NotificationWithIcon(
                "success",
                t("invoice_added"),
                t("invoice_added_successfully"),
              );
              reloadCallback();
            } else {
              handleApiError(response, t);
            }
            // Hide Modal
            setModalVisible(false);
            setCart([]);
            resetTagModal();
          })
          .catch((error) => handleApiError(error, t))
          .finally(() => {
            // Set Loading False
            setLoadingBTN(false);
          });
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const getTotalQuantityForProduct = (items, productId) =>
    items
      .filter((item) => Number(item.product_id) === Number(productId))
      .reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const cartReducer = (product, action) => {
    setLoading(true);
    let updatedCart = JSON.parse(JSON.stringify(cart));
    const productId = product.product_id || product.id;
    const productCartKey =
      product.cart_key ||
      makeVariantKey(productId, product.selectedTagIds || product.tags || []);
    const indexFound = updatedCart.findIndex(
      (cartItem) => cartItem.cart_key === productCartKey,
    );

    if (action === "add") {
      const invoiceItem = buildInvoiceItem(
        product,
        product.selectedTagIds || [],
      );
      const totalQtyForProduct = getTotalQuantityForProduct(
        updatedCart,
        productId,
      );

      if (indexFound === -1) {
        if (
          is_stock_management_active &&
          invoiceItem.is_stockable &&
          totalQtyForProduct >= Number(invoiceItem.stock || 0)
        ) {
          showAppError(t("insufficient_quantity"));
        } else {
          updatedCart.push({ ...invoiceItem, quantity: 1 });
        }
      } else if (
        is_stock_management_active &&
        updatedCart[indexFound].is_stockable &&
        totalQtyForProduct >= Number(updatedCart[indexFound].stock || 0)
      ) {
        showAppError(t("insufficient_quantity"));
      } else {
        updatedCart[indexFound].quantity++;
      }
    } else if (indexFound !== -1 && action === "inc") {
      const totalQtyForProduct = getTotalQuantityForProduct(
        updatedCart,
        productId,
      );
      if (
        is_stock_management_active &&
        updatedCart[indexFound].is_stockable &&
        totalQtyForProduct >= Number(updatedCart[indexFound].stock || 0)
      ) {
        showAppError(t("insufficient_quantity"));
      } else {
        updatedCart[indexFound].quantity++;
      }
    } else if (indexFound !== -1 && action === "dec") {
      const newQty = Number(updatedCart[indexFound].quantity || 0) - 1;
      if (newQty <= 0) {
        updatedCart.splice(indexFound, 1);
      } else {
        updatedCart[indexFound].quantity = newQty;
      }
    } else if (indexFound !== -1 && action === "remove") {
      updatedCart.splice(indexFound, 1);
    } else if (indexFound !== -1 && action === "update_options") {
      const newTagIds = normalizeTagIds(product.selectedTagIds);
      const currentItem = updatedCart[indexFound];
      const selectedTags = (currentItem.available_tags || []).filter((tag) =>
        hasTagId(newTagIds, tag.id),
      );
      const newVariantKey = makeVariantKey(currentItem.product_id, newTagIds);
      const duplicateIndex = updatedCart.findIndex(
        (item, index) =>
          item.cart_key === newVariantKey && index !== indexFound,
      );

      if (duplicateIndex !== -1) {
        updatedCart[duplicateIndex].quantity =
          Number(updatedCart[duplicateIndex].quantity || 0) +
          Number(currentItem.quantity || 0);
        updatedCart.splice(indexFound, 1);
      } else {
        updatedCart[indexFound] = {
          ...currentItem,
          tags: selectedTags,
          selectedTagIds: newTagIds,
          cart_key: newVariantKey,
        };
      }
    }
    setCart(updatedCart);
    setLoading(false);
  };

  const handleProductSelect = (id) => {
    const product = productsData.find((product) => product.id === id);
    if (!product) return;

    const productForCart = buildInvoiceItem(product, []);
    const availableTags = getAvailableTags(productForCart);

    if (availableTags.length > 0) {
      setModalMode("add");
      setSelectedProductForTags(productForCart);
      setSelectedTagsForModal([]);
      setTagModalVisible(true);
    } else {
      cartReducer(productForCart, "add");
    }

    addInvoiceForm.setFieldsValue({
      product: "",
    });
  };

  const openEditTagModalForCartItem = (cartItem) => {
    setModalMode("edit");
    setSelectedProductForTags(cartItem);
    setSelectedTagsForModal(
      tagIdsFrom(cartItem.tags || cartItem.selectedTagIds || []),
    );
    setTagModalVisible(true);
  };

  const handleAddToCartFromTagModal = () => {
    if (!selectedProductForTags) return;

    if (modalMode === "edit") {
      cartReducer(
        {
          cart_key: selectedProductForTags.cart_key,
          selectedTagIds: selectedTagsForModal,
        },
        "update_options",
      );
    } else {
      cartReducer(
        buildInvoiceItem(selectedProductForTags, selectedTagsForModal),
        "add",
      );
    }

    resetTagModal();
  };

  const SubtotalDisplay = ({ record }) => {
    let subTotal = record.price * record.quantity;

    if (record.is_taxable && record.taxes) {
      const totalTaxRate = record.taxes.reduce(
        (sum, tax) => sum + Number(tax.rate),
        0,
      );
      subTotal += subTotal * (totalTaxRate / 100);
    }

    return roundOff(subTotal);
  };

  const tableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: 85,
      render: (record) => {
        return (
          <>
            <img
              alt={record.name_en}
              src={record.image ? record.image : "/images/image-not-found.png"}
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>{appConfig.direction === "ltr" ? record.name_en : record.name}</>
        );
      },
    },
    {
      title: t("options"),
      align: "center",
      render: (record) =>
        (record.available_tags || []).length > 0 ? (
          <div>
            <Button
              icon={<TagsOutlined />}
              type="link"
              onClick={() => openEditTagModalForCartItem(record)}
            >
              {record.tags && record.tags.length > 0
                ? `${record.tags.length} ${t("selected")}`
                : t("select_options")}
            </Button>
            {record.tags && record.tags.length > 0 && (
              <div style={{ marginTop: 6 }}>
                {record.tags.map((tag) => (
                  <span
                    key={tag.id ?? tag}
                    style={{
                      display: "inline-block",
                      marginRight: 6,
                      marginBottom: 4,
                      padding: "2px 8px",
                      background: "var(--color-bg)",
                      borderRadius: 12,
                      fontSize: 12,
                    }}
                  >
                    {appConfig.direction === "rtl"
                      ? tag.name_ar || tag.name
                      : tag.name || tag.name_en}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: t("unit_price"),
      render: (record) => {
        return (
          <>
            <CurrencyFormatter value={record.price} />
          </>
        );
      },
    },
    {
      title: t("quantity"),
      render: (record) => {
        return (
          <>
            <QuantityStepper
              value={record.quantity}
              min={1}
              decreaseDisabled={record.quantity <= 1}
              increaseDisabled={
                is_stock_management_active && record.is_stockable
                  ? record.stock <= record.quantity
                  : false
              }
              onDecrease={() => cartReducer(record, "dec")}
              onIncrease={() =>
                is_stock_management_active && record.is_stockable
                  ? record.stock > record.quantity && cartReducer(record, "inc")
                  : cartReducer(record, "inc")
              }
            />
          </>
        );
      },
    },
    {
      title: t("price_including_tax"),
      render: (record) => {
        return (
          <>
            <CurrencyFormatter value={SubtotalDisplay({ record })} />
          </>
        );
      },
    },
    {
      title: t("action"),
      align: "center",
      width: 85,
      render: (record) => {
        return (
          <Space>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => cartReducer(record, "remove")}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      {(authUser.can_add_pay_sales_invoice ||
        authUser.type === UserType.BusinessOwner) && (
        <Button
          key="open-sales-invoice-modal"
          type="primary"
          onClick={showModal}
        >
          + {t("invoice_add")}
        </Button>
      )}
      <Modal
        title={t("invoice_add")}
        open={modalVisible}
        onCancel={handleCancel}
        width={1200}
        style={{
          top: "20px",
        }}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="save_as_draft"
            type="primary"
            loading={loadingBTN}
            onClick={() => handleAdd(1)}
          >
            {t("save_as_draft")}
          </Button>,
          <Button
            key="issue_invoice"
            type="primary"
            loading={loadingBTN}
            onClick={() => handleAdd(2)}
          >
            {t("issue_invoice")}
          </Button>,
        ]}
      >
        <Form
          name="sales-invoice-form"
          layout="vertical"
          className="app-w-full"
          form={addInvoiceForm}
        >
          <Row gutter={[20, 20]}>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <Form.Item
                name="customer_id"
                label={t("customer_name")}
                rules={[
                  {
                    required: true,
                    message: t("validate_customer_name_required"),
                  },
                ]}
                hasFeedback
                className="app-w-full"
              >
                <Select
                  showSearch
                  allowClear
                  options={customerData.map((customer) => {
                    return {
                      label: customer.name_ar,
                      value: customer.id,
                    };
                  })}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <Form.Item
                name="invoice_due_date"
                label={t("due_date")}
                rules={[
                  {
                    required: true,
                    message: t("validate_date_required"),
                  },
                ]}
                hasFeedback
              >
                <DatePicker
                  disabledDate={(current) => {
                    return current && current < moment().add(-1, "day");
                  }}
                  format="YYYY-MM-DD"
                  allowClear
                  className="app-w-full"
                />
              </Form.Item>
            </Col>
            <Col
              className="gutter-row"
              xs={24}
              sm={24}
              md={12}
              lg={12}
              xl={8}
              xxl={8}
            >
              <Form.Item
                name="product"
                label={t("product_add")}
                rules={[
                  cart.length < 1 && {
                    required: true,
                    message: t("validate_product_name_required"),
                  },
                ]}
              >
                <Select
                  showSearch
                  allowClear
                  options={productsData.map((product) => {
                    return {
                      disabled:
                        is_stock_management_active && product.is_stockable
                          ? product.stock > 0
                            ? false
                            : true
                          : false,

                      label:
                        appConfig.direction === "ltr"
                          ? `${product.name_en} ${
                              is_stock_management_active && product.is_stockable
                                ? "(" + product.stock + ")"
                                : ""
                            }`
                          : `${product.name} ${
                              is_stock_management_active && product.is_stockable
                                ? "(" + product.stock + ")"
                                : ""
                            }`,
                      value: product.id,
                    };
                  })}
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  onChange={handleProductSelect}
                />
              </Form.Item>
            </Col>
            {cart.length > 0 && (
              <Col
                className="gutter-row"
                xs={24}
                sm={24}
                md={24}
                lg={24}
                xl={24}
                xxl={24}
              >
                <div
                  style={{
                    fontWeight: "bold",
                    paddingBottom: "8px",
                  }}
                >
                  {t("items")}
                </div>
                <Table
                  columns={tableColumns}
                  dataSource={cart}
                  loading={loading}
                  rowKey={(record) => record.cart_key}
                  pagination={false}
                />
                <div
                  style={{
                    border: "1px solid var(--color-border)",
                    marginTop: "30px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      borderBottom: "1px solid var(--color-border)",
                    }}
                  >
                    {t("total_without_tax")}
                    <span>
                      <CurrencyFormatter value={totalAmount - totalTax} />
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      borderBottom: "1px solid var(--color-border)",
                    }}
                  >
                    <div>
                      {t("total_taxes")}

                      <Popover
                        content={<TaxCalculator data={cart} />}
                        trigger="click"
                      >
                        <InfoCircleOutlined className="app-info-icon" />
                      </Popover>
                    </div>
                    <span>
                      <CurrencyFormatter value={totalTax} />
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "10px",
                      color: "var(--color-primary-hover)",
                    }}
                  >
                    {authUser.is_vat_exempt ? t("total") : t("total_with_tax")}

                    <b>
                      <CurrencyFormatter value={totalAmount} />
                    </b>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        </Form>
      </Modal>

      <TagOptionsModal
        t={t}
        appConfig={appConfig}
        open={tagModalVisible}
        onClose={resetTagModal}
        product={selectedProductForTags}
        options={getAvailableTags(selectedProductForTags)}
        selected={selectedTagsForModal}
        onChangeSelected={setSelectedTagsForModal}
        onConfirm={handleAddToCartFromTagModal}
        title={t("select_options")}
        confirmText={selectedTagsForModal.length > 0 ? t("add") : t("skip")}
        cancelText={t("cancel")}
      />
    </>
  );
};

export default AddInvoice;
